import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clearPortalQueryCache, prefetchJsonCached } from '../utils/portalQueryCache';
import { TOOLS_CACHE_URL, usePlatformAccessChanged, usePlatformAccessSync } from '../utils/platformAccessSync';
import { DEFAULT_POST_LOGIN_PATH, resolvePostLoginPath } from '../utils/postLoginRedirect';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastUserSignatureRef = useRef('');
  const [provider, setProvider] = useState({
    provider: 'LocalDemo',
    authMode: 'Local',
    loginUrl: '/api/auth/login',
    logoutUrl: '/api/auth/logout',
    tenantUrl: '',
    kinde: { enabled: false, scopes: [] },
  });

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const loadProvider = useCallback(async () => {
    const response = await fetch('/api/auth/provider-config', { credentials: 'include' });
    if (!response.ok) {
      return null;
    }

    const config = await response.json();
    setProvider(config);
    return config;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'include' });
      if (!response.ok) {
        setUser(null);
        lastUserSignatureRef.current = '';
        clearPortalQueryCache();
        return null;
      }

      const profile = await response.json();
      const nextSignature = `${(profile?.email || '').trim().toLowerCase()}|${(profile?.displayName || '').trim()}|${(profile?.role || '').trim()}`;
      if (lastUserSignatureRef.current && lastUserSignatureRef.current !== nextSignature) {
        clearPortalQueryCache();
      }
      lastUserSignatureRef.current = nextSignature;
      setUser(profile);
      prefetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 60_000 });
      fetch('/api/sso/prepare', { method: 'POST', credentials: 'include' }).catch(() => {});
      return profile;
    } catch {
      setUser(null);
      lastUserSignatureRef.current = '';
      clearPortalQueryCache();
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.allSettled([loadProvider(), refresh()]).finally(() => setIsLoading(false));
  }, [loadProvider, refresh]);

  usePlatformAccessSync(Boolean(user), user?.platformAccessRevision);

  const onPlatformAccessChanged = useCallback(() => {
    prefetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 60_000 });
    void refresh();
  }, [refresh]);

  usePlatformAccessChanged(onPlatformAccessChanged);

  const login = useCallback(async (email, password, returnUrl) => {
    if (provider?.kinde?.enabled) {
      throw new Error('Kinde login must be started with redirect.');
    }

    const safeReturnUrl = resolvePostLoginPath(returnUrl);
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnUrl: safeReturnUrl }),
    });

    if (!response.ok) {
      throw new Error('Sign-in failed. Please check your email and password.');
    }

    const result = await response.json();
    setUser(result.profile);
    prefetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 60_000 });
    return resolvePostLoginPath(result.redirectUrl);
  }, [provider?.kinde?.enabled]);

  const startProviderLogin = useCallback((returnUrl, options = {}) => {
    const safeReturnUrl = resolvePostLoginPath(returnUrl || DEFAULT_POST_LOGIN_PATH);
    const query = new URLSearchParams({ returnUrl: safeReturnUrl });
    if (options.loginHint) {
      query.set('loginHint', options.loginHint);
    }
    if (options.connectionId) {
      query.set('connectionId', options.connectionId);
    }
    if (options.disableConnectionId) {
      query.set('disableConnectionId', 'true');
    }
    if (options.prompt) {
      query.set('prompt', options.prompt);
    } else if (options.forcePromptLogin) {
      query.set('prompt', 'login');
    }
    if (options.maxAge !== undefined && options.maxAge !== null) {
      query.set('maxAge', String(options.maxAge));
    }

    if (options.stepUp) {
      query.set('stepUp', options.stepUp);
    }

    const target = `${provider.loginUrl}?${query.toString()}`;
    window.location.assign(target);
  }, [provider]);

  const logout = useCallback(async (returnUrl) => {
    // Prevent ProtectedRoute from bouncing to /login?returnUrl=<current page>
    // before the hard Kinde/logout redirect finishes.
    setIsLoggingOut(true);

    let redirectUrl = '/login';
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // Logout always lands on the clean login page (no nested returnUrl).
        body: JSON.stringify({ returnUrl: '/login' }),
      });

      if (response.ok) {
        const result = await response.json();
        redirectUrl = result.redirectUrl || provider?.logoutUrl || '/login';
      } else if (provider?.logoutUrl) {
        redirectUrl = `${provider.logoutUrl}?returnUrl=${encodeURIComponent('/login')}`;
      }
    } catch {
      if (provider?.logoutUrl) {
        redirectUrl = `${provider.logoutUrl}?returnUrl=${encodeURIComponent('/login')}`;
      }
    }

    setUser(null);
    lastUserSignatureRef.current = '';
    clearPortalQueryCache();

    // Full browser navigation is required for OIDC end_session (and /api/auth/kinde/*).
    // React Router must not swallow these paths.
    const needsHardRedirect =
      /^https?:\/\//i.test(redirectUrl)
      || redirectUrl.startsWith('/api/auth/kinde/logout')
      || redirectUrl.includes('/api/auth/kinde/logout');

    if (needsHardRedirect) {
      window.location.assign(redirectUrl);
      return null;
    }

    setIsLoggingOut(false);
    return '/login';
  }, [provider]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      isLoggingOut,
      provider,
      login,
      startProviderLogin,
      logout,
      refresh,
    }),
    [user, isLoading, isLoggingOut, provider, login, startProviderLogin, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
